import { useQuery } from '@tanstack/react-query';
import type { ProductSummary } from '@/lib/api-admin/types';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import { createdFromParam, createdToParam } from '../lib/createdRange';
import { toProductSortOption, type ProductSortOption } from '../lib/productSort';


export interface ProductsQueryParams {
  page?: number;
  size?: number;
  sort?: string;
  keyword?: string;
  saleStatus?: 'ON_SALE' | 'SOLD_OUT' | 'PRESALE' | 'SALE_ENDED' | 'HIDDEN' | 'PREPARING' | null;
  /** QA-374 — 판매상태 다중선택. 1개면 saleStatus 로, 2개 이상은 saleStatuses(반복 파라미터)로 보낸다. */
  saleStatuses?: ReadonlyArray<'ON_SALE' | 'SOLD_OUT' | 'PRESALE' | 'SALE_ENDED' | 'HIDDEN' | 'PREPARING'>;
  productType?: 'GENERAL' | 'PRESALE' | 'EVENT' | 'SET' | 'CONCERT' | null;
  categoryId?: string | null;
  priceMin?: number | null;
  priceMax?: number | null;
  stockMin?: number | null;
  stockMax?: number | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  updatedFrom?: string | null;
  updatedTo?: string | null;
  artistId?: string | null;
  hasArtist?: boolean | null;
  displayed?: boolean | null;
  // QA-89(ID89) 조회조건 확장 — 상품코드/바코드 정확일치 + 옵션보유 필터(be-catalog QA-89 계약).
  productCode?: string | null;
  barcode?: string | null;
  // 자체상품코드 정확일치(be-catalog selfProductCode, 최대 50자).
  selfProductCode?: string | null;
  hasOption?: boolean | null;
}

interface ProductsPage {
  items: readonly ProductSummary[];
  meta: ApiMeta;
}

export const productsQueryKey = (params: ProductsQueryParams) =>
  ['admin', 'products', params] as const;

interface AdminListQuery {
  page?: number;
  size?: number;
  sort?: ProductSortOption;
  keyword?: string;
  saleStatus?: 'ON_SALE' | 'SOLD_OUT' | 'PRESALE' | 'SALE_ENDED' | 'HIDDEN' | 'PREPARING';
  saleStatuses?: Array<'ON_SALE' | 'SOLD_OUT' | 'PRESALE' | 'SALE_ENDED' | 'HIDDEN' | 'PREPARING'>;
  productType?: 'GENERAL' | 'PRESALE' | 'EVENT' | 'SET' | 'CONCERT';
  categoryId?: string;
  priceMin?: number;
  priceMax?: number;
  stockMin?: number;
  stockMax?: number;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  artistId?: string;
  hasArtist?: boolean;
  displayed?: boolean;
  productCode?: string;
  barcode?: string;
  selfProductCode?: string;
  hasOption?: boolean;
}

export const cleanQuery = (params: ProductsQueryParams): AdminListQuery => {
  const out: AdminListQuery = {};
  if (params.page) out.page = Math.max(0, params.page - 1);
  if (params.size) out.size = params.size;
  // 정렬 — BE 는 ProductSortOption enum(LATEST/OLDEST/PRICE_*)만 받는다. 대응 없으면 생략(BE 기본 LATEST).
  const sort = toProductSortOption(params.sort);
  if (sort) out.sort = sort;
  // ID61: keyword 는 trim 후 비어있지 않을 때만 전송. 공백/trailing space 가 BE LIKE 바인드에
  // 그대로 들어가면 유효 검색어인데도 0건이 반환되는 "검색 결과 미노출" 증상을 유발한다.
  if (params.keyword) {
    const trimmed = params.keyword.trim();
    if (trimmed) out.keyword = trimmed;
  }
  if (params.saleStatus) out.saleStatus = params.saleStatus;
  // QA-374 — 다중선택. BE 는 지금 saleStatus 하나만 받아, 1개일 때는 그 값으로 보낸다.
  // 2개 이상은 saleStatuses 로 보낸다(BE 반영 전에는 무시되어 판매상태로 거르지 않는다 — 전달 문서 참고).
  const statuses = [...new Set(params.saleStatuses ?? [])];
  if (statuses.length === 1 && !out.saleStatus) out.saleStatus = statuses[0]!;
  if (statuses.length > 1) out.saleStatuses = statuses;
  if (params.productType) out.productType = params.productType;
  if (params.categoryId) out.categoryId = params.categoryId;
  if (params.priceMin != null) out.priceMin = params.priceMin;
  if (params.priceMax != null) out.priceMax = params.priceMax;
  if (params.stockMin != null) out.stockMin = params.stockMin;
  if (params.stockMax != null) out.stockMax = params.stockMax;
  // 등록일 — BE 는 ISO 날짜-시각(Instant)을 받는다. 날짜만 보내면 400(createdRange 참고).
  const createdFrom = createdFromParam(params.createdFrom);
  const createdTo = createdToParam(params.createdTo);
  if (createdFrom) out.createdFrom = createdFrom;
  if (createdTo) out.createdTo = createdTo;
  // QA-374 — 수정일(등록일과 같은 KST 경계). BE updatedFrom/updatedTo 반영 필요.
  const updatedFrom = createdFromParam(params.updatedFrom);
  const updatedTo = createdToParam(params.updatedTo);
  if (updatedFrom) out.updatedFrom = updatedFrom;
  if (updatedTo) out.updatedTo = updatedTo;
  if (params.artistId) out.artistId = params.artistId;
  if (params.hasArtist != null) out.hasArtist = params.hasArtist;
  if (params.displayed != null) out.displayed = params.displayed;
  // QA-89 — 상품코드/바코드는 trim 후 비어있지 않을 때만 전송(공백 정확일치 0건 회피).
  if (params.productCode) {
    const trimmed = params.productCode.trim();
    if (trimmed) out.productCode = trimmed;
  }
  if (params.barcode) {
    const trimmed = params.barcode.trim();
    if (trimmed) out.barcode = trimmed;
  }
  if (params.selfProductCode) {
    const trimmed = params.selfProductCode.trim();
    if (trimmed) out.selfProductCode = trimmed;
  }
  if (params.hasOption != null) out.hasOption = params.hasOption;
  return out;
};

interface ProductsEnvelopeShape {
  items?: ProductSummary[];
  content?: ProductSummary[];
  totalElements?: number;
  number?: number;
  size?: number;
}

export const useProductsQuery = (params: ProductsQueryParams) =>
  useQuery<ProductsPage>({
    queryKey: productsQueryKey(params),
    queryFn: async () => {
      const [data, meta] = await apiClient.request('get', '/admin/v1/products', {
        params: { query: cleanQuery(params) },
      });
      const payload = data as ProductsEnvelopeShape;
      const items = payload.items ?? payload.content ?? [];
      const mergedMeta: ApiMeta = meta?.pagination
        ? meta
        : {
            ...meta,
            pagination: {
              page: (payload.number ?? 0) + 1,
              size: payload.size ?? items.length,
              totalItems: payload.totalElements ?? items.length,
              totalPages: payload.totalElements != null && payload.size
                ? Math.ceil(payload.totalElements / payload.size)
                : 1,
            },
          };
      return { items, meta: mergedMeta };
    },
  });
